import {
  type CreateParticipantDocumentRequest,
  type Label,
  LabelType,
  ParticipantDocumentStatus,
  type Template,
  TemplateStatus,
  TemplateType,
} from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  dataAttr,
  MultiSelect,
  type MultiSelectOption,
  type OnChangeRequest,
  ScrollArea,
  Spin,
} from '@piccolohealth/ui';
import { P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaFile } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useCreateParticipantDocumentMutation } from '../../graphql/hooks/useCreateParticipantDocumentMutation';
import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useTemplatesQuery } from '../../graphql/hooks/useTemplatesQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantDocumentTemplatePreview } from '../templates/documents/ParticipantDocumentTemplatePreview';
import { TemplateStatusTag } from '../templates/TemplateStatusTag';

interface TemplateLabelFilterControlProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const TemplateLabelFilterControl = (props: TemplateLabelFilterControlProps) => {
  const { value, onChange } = props;

  const { organization } = useAppContext();
  const { data, isLoading } = useLabelsQuery({
    organizationId: organization.id,
    type: LabelType.Template,
  });

  const options: MultiSelectOption<Label>[] = React.useMemo(() => {
    const labels = (data?.organization?.labels as Label[]) ?? [];
    return labels.map((label) => {
      return {
        label: label.name,
        value: label.id,
        raw: label,
        color: label.color,
      };
    });
  }, [data?.organization?.labels]);

  const selectedValues: MultiSelectOption<Label>[] = React.useMemo(() => {
    return (value ?? []).flatMap((label) => {
      return options.find((o) => P.isEqual(o.raw.id, label)) ?? [];
    });
  }, [value, options]);

  const onChangePrime = React.useCallback(
    (req: OnChangeRequest<Label>) => {
      onChange(req.values.map(({ raw }) => raw.id));
    },
    [onChange],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChangePrime}
      size='sm'
      placeholder='All labels'
      optionVariant='tag'
    />
  );
};

interface TemplateSidebarProps {
  templates: Template[];
  activeTemplateId: string | undefined;
  onClick: (template: Template) => void;
}

const TemplatesSidebar = (props: TemplateSidebarProps) => {
  const { templates, activeTemplateId, onClick } = props;

  const [name, setName] = React.useState('');
  const [labels, setLabels] = React.useState<string[]>([]);

  const filteredTemplates = React.useMemo(() => {
    return templates.filter((template) => {
      const nameMatches = template.name.toLowerCase().includes(name.trim().toLowerCase());
      const labelsMatch =
        P.isEmpty(labels) || template.labels.some((label) => labels.includes(label.id));
      return nameMatches && labelsMatch;
    });
  }, [labels, name, templates]);

  return (
    <ChakraV3.Stack h='full' w='280px' gap={4} flexShrink={0}>
      <ChakraV3.Stack>
        <ChakraV3.FormField label='Name'>
          <ChakraV3.Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Search templates...'
            size='sm'
          />
        </ChakraV3.FormField>

        <ChakraV3.FormField label='Labels'>
          <TemplateLabelFilterControl value={labels} onChange={setLabels} />
        </ChakraV3.FormField>
      </ChakraV3.Stack>

      <ChakraV3.Separator />

      <ScrollArea flexDir='column' overflowY='auto'>
        <ChakraV3.Stack pr={4} gap={0}>
          <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
            Participant
          </ChakraV3.Text>
          {P.isEmpty(filteredTemplates) && (
            <ChakraV3.Text color='secondary' fontSize='sm'>
              No templates found...
            </ChakraV3.Text>
          )}
          {filteredTemplates.map((template) => (
            <ChakraV3.Button
              onClick={() => onClick(template)}
              key={template.id}
              {...{ 'data-active': dataAttr(activeTemplateId === template.id) }}
              variant='ghost'
              color='gray.600'
              size='sm'
              justifyContent='start'
              whiteSpace='initial'
              h='auto'
              px={3}
              py={2}
              rounded='md'
              fontSize='sm'
              fontWeight='semibold'
              textAlign='left'
            >
              <FaFile />
              {template.name}
            </ChakraV3.Button>
          ))}
        </ChakraV3.Stack>
      </ScrollArea>
    </ChakraV3.Stack>
  );
};

type FormValues = CreateParticipantDocumentRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  templateId: z.string(),
  participantId: z.string(),
  name: z.string(),
  labelIds: z.array(z.string()),
  authorIds: z.array(z.string()),
  reviewerIds: z.array(z.string()),
  status: z.enum(ParticipantDocumentStatus),
});

interface ParticipantDocumentCreateModalProps {
  participantId: string;
}

export const ParticipantDocumentCreateModal = createModal<ParticipantDocumentCreateModalProps>(
  (props) => {
    const { participantId, modal } = props;
    const { errorToast, successToast, organization } = useAppContext();

    const { hide, remove, visible } = modal;
    const navigate = useNavigate();

    const query = useTemplatesQuery({
      organizationId: organization.id,
      request: {
        type: [TemplateType.ParticipantDocument],
        status: [TemplateStatus.Published],
      },
    });

    const mutation = useCreateParticipantDocumentMutation();

    const methods = useForm<FormValues>({
      defaultValues: {
        participantId,
        name: undefined,
        templateId: undefined,
        labelIds: [],
        authorIds: [],
        reviewerIds: [],
        status: ParticipantDocumentStatus.Draft,
      },
      mode: 'all',
      resolver: zodResolver(schema),
    });

    const { handleSubmit, watch, setValue } = methods;

    const activeTemplateId = watch('templateId');

    const templates = React.useMemo(() => {
      return (query.data?.organization?.templates.results as Template[]) ?? [];
    }, [query.data?.organization?.templates]);

    const activeTemplate = templates.find((template) => template.id === activeTemplateId);

    const selectTemplate = React.useCallback(
      (template: Template) => {
        setValue('templateId', template.id, { shouldDirty: true });
        setValue('name', template.name, { shouldDirty: true });
      },
      [setValue],
    );

    const onSubmit = React.useCallback(
      async (values: FormValues) => {
        await mutation
          .mutateAsync({
            organizationId: organization.id,
            request: values,
          })
          .then((res) => {
            hide();
            successToast('Document created successfully');
            void navigate(
              `/organizations/${organization.id}/participants/${participantId}/documents/${res.createParticipantDocument.id}`,
            );
          })
          .catch((err) => {
            errorToast(`Error creating document: ${err.message}`);
          });
      },
      [errorToast, hide, mutation, navigate, organization.id, participantId, successToast],
    );

    React.useEffect(() => {
      const [template] = templates;

      if (template) {
        selectTemplate(template);
      }
    }, [templates, selectTemplate]);

    const content = P.run(() => {
      if (query.isLoading) {
        return <Spin />;
      }

      if (query.error) {
        return <PiccoloError error={query.error} />;
      }

      if (P.isEmpty(templates)) {
        return <ChakraV3.Text fontSize='sm'>No templates found</ChakraV3.Text>;
      }

      return (
        <ChakraV3.HStack w='full' h='full' gap={4} align='start'>
          <TemplatesSidebar
            templates={templates}
            activeTemplateId={activeTemplateId}
            onClick={selectTemplate}
          />
          <ChakraV3.Separator orientation='vertical' h='full' />
          <ChakraV3.Stack w='full' h='full' align='center'>
            <ChakraV3.Stack h='full' w='full' overflowY='auto' layerStyle='fade'>
              {activeTemplateId && (
                <ParticipantDocumentTemplatePreview
                  templateId={activeTemplateId}
                  participantId={participantId}
                />
              )}
            </ChakraV3.Stack>
            <ChakraV3.HStack
              w='full'
              bg='gray.50'
              layerStyle='outline.subtle'
              px={8}
              py={4}
              rounded='md'
              align='end'
            >
              {activeTemplate && (
                <ChakraV3.Stack w='full'>
                  <ChakraV3.Text fontSize='lg' fontWeight='bold'>
                    {activeTemplate?.name}
                  </ChakraV3.Text>
                  <ChakraV3.HStack>
                    <TemplateStatusTag status={activeTemplate.status} />
                    {activeTemplate.scoringChecklist && (
                      <ChakraV3.Tag size='sm' colorPalette='purple'>
                        Scoring checklist
                      </ChakraV3.Tag>
                    )}
                    {activeTemplate.labels.map((label) => (
                      <ChakraV3.Tag key={label.id} size='sm' colorPalette={label.color}>
                        {label.name}
                      </ChakraV3.Tag>
                    ))}
                  </ChakraV3.HStack>
                </ChakraV3.Stack>
              )}
              <ChakraV3.Spacer />
              <ChakraV3.HStack>
                <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                  Close
                </ChakraV3.Button>
                <HookedSubmitButton>Use template</HookedSubmitButton>
              </ChakraV3.HStack>
            </ChakraV3.HStack>
          </ChakraV3.Stack>
        </ChakraV3.HStack>
      );
    });

    return (
      <ChakraV3.Dialog.Root
        open={visible}
        onOpenChange={hide}
        onExitComplete={remove}
        size='cover'
        scrollBehavior='inside'
      >
        <ChakraV3.Dialog.Backdrop />
        <ChakraV3.Dialog.Positioner>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ChakraV3.Dialog.Content>
                <ChakraV3.DialogTitleSubtitle
                  title='Create a new document'
                  subtitle='Please complete the following to proceed'
                />
                <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
              </ChakraV3.Dialog.Content>
            </form>
          </FormProvider>
        </ChakraV3.Dialog.Positioner>
      </ChakraV3.Dialog.Root>
    );
  },
);
